import { useMemo } from 'react';

import { ColumnType as AntdColumn } from 'antd/es/table/interface';

import { isDefined } from '@utils/isDefined';
import { ColumnRenderType, ColumnType } from '@components/Table/types';
import { RowText } from '@components/Table/RowText';
import { FilterIcon } from '@components/Table/filters/FilterDropDown/FilterIcon';
import { TableTruncatedText } from '@components/Table/TableTruncatedText/TableTruncatedText';
import type * as React from 'react';

interface Props {
    tableColumns: ColumnType<any>[];
}

interface Data {
    columns: MadeColumnType[];
}

export type MadeColumnType = Omit<
    AntdColumn<any>,
    'renderType' | 'makeColumnValue' | 'filterIconData' | 'filterIcon'
> & {
    filterIcon?: (filtered: boolean) => React.ReactNode;
};

export function useMakeColumns({ tableColumns }: Props): Data {
    function makeColumn({
        renderType,
        makeColumnValue,
        filterData,
        ...column
    }: ColumnType<any>): Omit<AntdColumn<any>, 'renderType'> {
        let render = column.render;
        if (!!renderType) {
            switch (renderType) {
                case ColumnRenderType.TruncatedWithShowMore: {
                    render = (value, record) => (
                        <TableTruncatedText
                            showMore={'text-button'}
                            maxLines={2}
                            value={!!makeColumnValue ? makeColumnValue(value, record) : value}
                        />
                    );
                    break;
                }
                case ColumnRenderType.Truncated: {
                    render = (value, record) => (
                        <TableTruncatedText
                            showMore={'click'}
                            maxLines={2}
                            value={!!makeColumnValue ? makeColumnValue(value, record) : value}
                        />
                    );
                    break;
                }
                case ColumnRenderType.RowText: {
                    render = (value, record) => (
                        <RowText text={!!makeColumnValue ? makeColumnValue(value, record) : value} />
                    );
                    break;
                }
                default: {
                    const type: undefined = renderType;
                    console.error(`Forgot add render for ${type}`);
                }
            }
        }

        return {
            ...column,
            render,
            filteredValue: filterData?.filteredValue,
            filterIcon:
                isDefined(filterData) && !filterData.withoutFilterCountIcon
                    ? (filtered: boolean) => (
                          <FilterIcon
                              filtered={filtered}
                              count={filterData.count}
                          />
                      )
                    : undefined,
        };
    }

    const columns: ColumnType<any>[] = useMemo(() => tableColumns.map(makeColumn), [tableColumns]);

    return {
        columns,
    };
}
